import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSnackbar } from 'notistack'
import { faCircleExclamation, faEye, faLock, faLockOpen, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useOnlineStatus } from '../Hooks/useOnlineStatus'
import { useMutationQueue } from '../../offline/useMutationQueue'
import { useSyncProgress } from '../../offline/syncStatus'
import { offlineDb, type SyncIssue } from '../../offline/db'
import { RootState } from '../../state/store'
import { setSelectedDate, setSelectedShift, setShiftEditability } from '../../state/reducers/shiftReducer'
import { settingsAjax } from '../../ajax/SettingsAjax'
import { ErrorResponse } from '../../DTOs/Common/ErrorResponse'
import { createAppRoutes, editControlAreas } from '../../utilities/appRoutes'
import { isViewOnlyPage } from '../../utilities/helpers/roleUtilities'
import { pageOf, routeChainAt } from '../../utilities/helpers/routeAccess'
import { useGlobalModal } from '../Contexts/GlobalModalContext'
import ConflictResolutionModal from './ConflictResolutionModal'
import SyncIssuesModal from './SyncIssuesModal'
import { resolveStatusConditions, StatusCondition, StatusTone } from './statusBannerState'
import { useDrillBlastModules } from '../Hooks/useDrillBlastSettings';

const PINNED_CLASSES = 'sticky top-0 z-20'

const ROW_CLASSES = 'w-full shrink-0 overflow-hidden flex flex-nowrap items-center justify-center gap-2 px-4 py-2 compact:py-1 text-sm font-medium border-b transition-colors'

const TONE_CLASSES: Record<StatusTone, string> = {
  danger: 'bg-theme-danger text-theme-primary-contrast border-theme-danger-hover',
  info: 'bg-theme-info text-theme-primary-contrast border-theme-info-hover',
  warning: 'bg-theme-warning text-theme-primary-contrast border-theme-warning-hover',
  neutral: 'bg-theme-surface-2 text-theme-text border-theme-border',
}

const CHIP_CLASSES: Record<StatusTone, string> = {
  danger: 'bg-white/25',
  info: 'bg-white/25',
  warning: 'bg-white/25',
  neutral: 'bg-theme-bg',
}

const CONTROL_CLASSES = 'h-5 shrink-0 inline-flex items-center gap-1 rounded-full px-2 text-xs font-bold leading-none'

const shiftLabel = (shift: string) => (shift === 'DAY' ? 'day' : shift === 'NIGHT' ? 'night' : 'afternoon')

function conditionIcon(condition: StatusCondition) {
  if (condition.kind === 'syncing') return faSpinner
  if (condition.kind === 'syncIssues') return faTriangleExclamation
  if (condition.kind === 'shiftLocked') return faLock
  if (condition.kind === 'viewOnly') return faEye
  return faCircleExclamation
}

function chipLabel(condition: StatusCondition): string {
  switch (condition.kind) {
    case 'syncing': return 'Syncing'
    case 'offline': return 'Offline'
    case 'syncIssues': return `${condition.count} ${condition.count === 1 ? 'issue' : 'issues'}`
    case 'pending': return `${condition.count} pending`
    case 'shiftLocked': return condition.exists ? 'Shift locked' : 'Shift not synced'
    case 'viewOnly': return 'View only'
  }
}

const StatusBanner = () => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { showModal, hideModal } = useGlobalModal()
  const isOnline = useOnlineStatus()
  const { pendingCount } = useMutationQueue()
  const { isSyncing, total, done } = useSyncProgress()
  const [isUnlocking, setIsUnlocking] = useState(false)
  const user = useSelector((state: RootState) => state.authReducer.user)
  const telemetryEnabled = useSelector((state: RootState) => state.authReducer.roleConfig?.telemetryEnabled)
  const drillBlastModules = useDrillBlastModules();
  const routes = useMemo(() => createAppRoutes({ telemetryEnabled, drillBlastModules }), [telemetryEnabled, drillBlastModules])
  const page = pageOf(routeChainAt(routes, pathname))

  const issuesRaw = useLiveQuery(() => offlineDb.syncIssues.orderBy('createdAt').toArray(), [])
  const {
    isLocked, canToggleLock, shiftExists, selectedDate, selectedShift, provisionalShiftDate, provisionalShift,
  } = useSelector((state: RootState) => state.shift)

  const prevIssueCountRef = useRef<number>(-1)
  useEffect(() => {
    if (issuesRaw === undefined) return
    const prev = prevIssueCountRef.current
    if (prev >= 0 && issuesRaw.length > prev) {
      const added = issuesRaw.length - prev
      enqueueSnackbar(
        `${added} offline change${added === 1 ? '' : 's'} need${added === 1 ? 's' : ''} your attention. Open the sync issues from the status bar at the top of the screen.`,
        { variant: 'warning', autoHideDuration: 8000 }
      )
    }
    prevIssueCountRef.current = issuesRaw.length
  }, [issuesRaw, enqueueSnackbar])

  const openConflictModal = (issue: SyncIssue) => {
    showModal({
      content: () => <ConflictResolutionModal issue={issue} onClose={hideModal} />,
      tailwindMaxWidthClass: 'max-w-2xl',
    })
  }

  const openIssuesModal = () => {
    showModal({
      content: () => <SyncIssuesModal onClose={hideModal} onResolveConflict={openConflictModal} />,
      tailwindMaxWidthClass: 'max-w-3xl',
    })
  }

  const returnToProvisionalShift = () => {
    if (!provisionalShiftDate || !provisionalShift) return
    dispatch(setSelectedDate(provisionalShiftDate))
    dispatch(setSelectedShift(provisionalShift))
  }

  const unlockShift = async () => {
    const date = DateTime.fromISO(selectedDate)
    const backendCode = selectedShift === 'DAY' ? 'D' : 'N'
    setIsUnlocking(true)
    try {
      await settingsAjax.unlockShift(date, backendCode)
      const res = await settingsAjax.getShiftEditability(date, backendCode)
      if (!ErrorResponse.isErrorResponse(res)) dispatch(setShiftEditability(res))
    } finally {
      setIsUnlocking(false)
    }
  }

  const conditions = resolveStatusConditions({
    isOnline,
    isSyncing,
    syncTotal: total,
    syncDone: done,
    pendingCount,
    syncIssueCount: issuesRaw?.length ?? 0,
    isShiftPage: !!page?.isShiftPage,
    isShiftLocked: isLocked,
    shiftExists,
    canToggleLock,
    selectedDate,
    selectedShift,
    provisionalShiftDate,
    provisionalShift,
    isViewOnly: isViewOnlyPage(user, editControlAreas(page)),
  })

  const [primary, ...secondaries] = conditions

  if (!primary) {
    return <div aria-hidden className={`${ROW_CLASSES} border-transparent`}>&nbsp;</div>
  }

  const message = (() => {
    switch (primary.kind) {
      case 'syncing':
        return (
          <>
            {primary.done > 0 && primary.done < primary.total
              ? `Syncing your offline changes - ${primary.done} of ${primary.total} done.`
              : `Syncing ${primary.total} offline ${primary.total === 1 ? 'change' : 'changes'}.`}
            <span className="hidden sm:inline"> You can carry on using Eclipse - just don't close the browser until it finishes.</span>
          </>
        )
      case 'offline':
        return (
          <>
            <span className="hidden sm:inline">You're offline — edits are queued and will sync when you reconnect</span>
            <span className="sm:hidden">Offline — edits are queued</span>
          </>
        )
      case 'syncIssues':
        return `${primary.count} sync ${primary.count === 1 ? 'issue needs' : 'issues need'} your attention`
      case 'pending':
        return `${primary.count} offline ${primary.count === 1 ? 'change is' : 'changes are'} waiting to sync.`
      case 'shiftLocked':
        return (
          <>
            <strong>{DateTime.fromISO(primary.dateIso).toFormat('dd MMM yyyy')} · {shiftLabel(primary.shift)} shift</strong>
            {primary.exists
              ? <> is locked<span className="hidden sm:inline"> — view only</span></>
              : <> has not reached the server<span className="hidden sm:inline"> — view only until it syncs</span></>}
          </>
        )
      case 'viewOnly':
        return (
          <>
            This page is <strong>view only</strong>
            <span className="hidden sm:inline"> - you do not have permissions to edit.</span>
          </>
        )
    }
  })()

  const chipClasses = `${CONTROL_CLASSES} ${CHIP_CLASSES[primary.tone]}`
  const buttonClasses = `${chipClasses} hover:bg-white/40 cursor-pointer transition`

  const action = (condition: StatusCondition) => {
    switch (condition.kind) {
      case 'offline':
        return condition.returnTo && (
          <button type="button" onClick={returnToProvisionalShift} className={buttonClasses}>
            Back to {DateTime.fromISO(condition.returnTo.date).toFormat('dd MMM')} {shiftLabel(condition.returnTo.shift)} shift
          </button>
        )
      case 'syncIssues':
        return <button type="button" onClick={openIssuesModal} className={buttonClasses}>Review</button>
      case 'shiftLocked':
        return condition.canUnlock && (
          <button
            type="button"
            onClick={unlockShift}
            disabled={isUnlocking}
            className={`${CONTROL_CLASSES} bg-theme-warning hover:bg-theme-warning-hover disabled:opacity-50 text-theme-primary-contrast border border-theme-warning-hover cursor-pointer transition`}
          >
            <FontAwesomeIcon icon={faLockOpen} />
            {isUnlocking ? 'Unlocking…' : 'Unlock'}
          </button>
        )
      default:
        return null
    }
  }

  return (
    <div className={`${ROW_CLASSES} ${PINNED_CLASSES} ${TONE_CLASSES[primary.tone]}`}>
      <FontAwesomeIcon icon={conditionIcon(primary)} spin={primary.kind === 'syncing'} className="shrink-0" />
      <span className="min-w-0 truncate">{message}</span>

      {primary.kind === 'offline' && primary.pendingCount > 0 && (
        <span className={chipClasses}>{primary.pendingCount} pending</span>
      )}

      {action(primary)}

      {secondaries.map(condition => (
        <Fragment key={condition.kind}>
          <span className={chipClasses}>
            <FontAwesomeIcon icon={conditionIcon(condition)} spin={condition.kind === 'syncing'} />
            {chipLabel(condition)}
          </span>
          {action(condition)}
        </Fragment>
      ))}
    </div>
  )
}

export default StatusBanner
